// pages/IdentityDetail/IdentityDetail.js
// Cross-device, cross-session identity deep-dive screen.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Fingerprint, RefreshCw, Users, Eye, Smile, Clock,
  Monitor, Camera, TrendingUp, BarChart2, Crosshair, X,
  Activity, ChevronLeft, ChevronRight, Box, GitMerge, Shield,
  Zap, Cpu, Layers, AlertTriangle, Target, Brain,
} from 'lucide-react';
import FaceMeshViewer from '../../components/FaceMeshViewer';
import FaceImg from '../../components/FaceImg';
import { ggosDeviceService } from '../../services/api';
import './IdentityDetail.css';

const TIME_WINDOWS = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

const MOOD_COLORS = {
  HAPPY: '#22c55e', JOYFUL: '#16a34a', CONTENT: '#34d399', AMUSED: '#10b981',
  NEUTRAL: '#94a3b8', BORED: '#78716c', DISTRACTED: '#a8a29e', DROWSY: '#a1a1aa',
  INTERESTED: '#60a5fa', CURIOUS: '#3b82f6', ATTENTIVE: '#2563eb',
  SAD: '#60a5fa', SURPRISED: '#fbbf24', FRUSTRATED: '#ef4444', IRRITATED: '#f87171',
  CONFUSED: '#a78bfa', ANGRY: '#dc2626', CONTEMPT: '#fb923c', FEARFUL: '#f472b6',
  DISGUSTED: '#a3e635', ANXIOUS: '#f97316',
};

const RELEVANCE_COLORS = { POSITIVE: '#22c55e', NEUTRAL: '#94a3b8', NEGATIVE: '#ef4444' };

const RECON_BADGES = {
  pending:              { label: 'Pending',  bg: '#fef3c7', color: '#92400e' },
  resolved:             { label: 'Resolved', bg: '#dcfce7', color: '#166534' },
  merged:               { label: 'Merged',   bg: '#ede9fe', color: '#5b21b6' },
  sanctified:           { label: 'Sanctified', bg: '#dbeafe', color: '#1e40af' },
  cross_device_linked:  { label: 'Cross-device', bg: '#fce7f3', color: '#9d174d' },
};

const MERGE_LABEL_INFO = {
  oracle:                  { label: 'Oracle',              bg: '#dcfce7', color: '#166534', icon: '\u{1F517}' },
  rekognition_confirmed:   { label: 'Rekognition',         bg: '#dbeafe', color: '#1e40af', icon: '\u2696\uFE0F' },
  session_reconciler:      { label: 'Reconciler',          bg: '#ede9fe', color: '#5b21b6', icon: '\u{1F504}' },
  cross_device:            { label: 'Cross-Device',        bg: '#fce7f3', color: '#9d174d', icon: '\u{1F4F1}' },
  fragmentation:           { label: 'Defrag',              bg: '#fef3c7', color: '#92400e', icon: '\u{1F9E9}' },
  fragment:                { label: 'Fragment',            bg: '#fef3c7', color: '#92400e', icon: '\u{1F9E9}' },
  gc_cleanup:              { label: 'GC Cleanup',          bg: '#f3f4f6', color: '#555',    icon: '\u{1F5D1}\uFE0F' },
};

/* helpers */
const fmtRel = (d) => {
  if (!d) return '\u2014';
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '\u2014';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014';
const fmtHour = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
const fmtDuration = (ms) => {
  if (!ms) return '\u2014';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
};
const shortId = (id) => id ? id.substring(0, 8) : '\u2014';
const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

/* Identity Strength score (0-100) */
function calcIdentityStrength(profile, mergeCount, devices) {
  let score = 0;
  if (profile.hasOracleEmbedding) score += 25;
  score += Math.min(profile.exemplarCount || 0, 3) * 7;
  score += Math.min((devices?.length || 0) - 1, 3) * 10;
  score += Math.min(profile.rideCount || 0, 100) * 0.15;
  score += Math.min(mergeCount || 0, 20) * 0.5;
  score += Math.min(profile.faceIdCount || 0, 30) * 0.2;
  score += (profile.confidence || 0) * 5;
  return Math.min(100, Math.round(score));
}

function strengthLabel(score) {
  if (score >= 80) return { label: 'Strong', color: '#16a34a' };
  if (score >= 60) return { label: 'Good', color: '#2563eb' };
  if (score >= 40) return { label: 'Moderate', color: '#d97706' };
  if (score >= 20) return { label: 'Weak', color: '#ea580c' };
  return { label: 'Nascent', color: '#dc2626' };
}

const SESSIONS_PER_PAGE = 20;

export default function IdentityDetail() {
  const { unifiedId } = useParams();
  const [searchParams] = useSearchParams();
  const fromDevice = searchParams.get('from');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeWindow, setTimeWindow] = useState(168);
  const [lightbox, setLightbox] = useState(null);
  const [sessionPage, setSessionPage] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const json = await ggosDeviceService.getIdentityDetail(unifiedId, { hours: timeWindow });
      if (!json.success) throw new Error(json.error || 'Failed to fetch');
      setData(json.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [unifiedId, timeWindow]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mergeSummary = useMemo(() => {
    if (!data?.mergeHistory?.length) return null;
    const byType = {};
    const cosines = [];
    let totalAbsorbed = 0;
    data.mergeHistory.forEach(m => {
      const lbl = m.mergeLabel || 'unknown';
      byType[lbl] = (byType[lbl] || 0) + 1;
      if (m.mergeCosine != null) cosines.push(m.mergeCosine);
      totalAbsorbed += m.sessionsContributed || 0;
    });
    return {
      count: data.mergeHistory.length,
      byType,
      cosines,
      avgCosine: cosines.length ? cosines.reduce((a, b) => a + b, 0) / cosines.length : null,
      minCosine: cosines.length ? Math.min(...cosines) : null,
      maxCosine: cosines.length ? Math.max(...cosines) : null,
      totalAbsorbed,
    };
  }, [data?.mergeHistory]);

  if (loading && !data) {
    return (
      <div className="id-page">
        <div className="id-loading"><RefreshCw size={16} className="spin" /> Loading identity...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="id-page">
        <div className="id-error">
          <Fingerprint size={28} />
          <p><strong>Identity not found</strong></p>
          <p style={{ fontSize: '0.8rem', color: '#888' }}>{error}</p>
          {fromDevice && (
            <Link to={`/ggos-devices/${fromDevice}?section=perception`} style={{ color: '#6366f1', fontSize: '0.8rem' }}>
              &larr; Back to device
            </Link>
          )}
        </div>
      </div>
    );
  }

  const { profile, devices, sessions, rides = [], moodTimeline, expressionDistribution, gaze, faceCrops, activityTimeline, totalCropCount, mergeHistory, embeddingQuality } = data;
  const exprMax = Math.max(...expressionDistribution.map(e => e.count), 1);
  const actMax = Math.max(...activityTimeline.map(a => a.sessions), 1);
  const totalPages = Math.ceil(sessions.length / SESSIONS_PER_PAGE);
  const pagedSessions = sessions.slice(sessionPage * SESSIONS_PER_PAGE, (sessionPage + 1) * SESSIONS_PER_PAGE);
  const identityStrength = calcIdentityStrength(profile, mergeSummary?.count || 0, devices);
  const strengthInfo = strengthLabel(identityStrength);

  return (
    <div className="id-page">
      {/* Breadcrumb */}
      <nav className="id-breadcrumb">
        <Link to="/ggos-devices">Devices</Link>
        <span className="id-breadcrumb-sep">&rsaquo;</span>
        {fromDevice ? (
          <>
            <Link to={`/ggos-devices/${fromDevice}?section=perception`}>{fromDevice}</Link>
            <span className="id-breadcrumb-sep">&rsaquo;</span>
          </>
        ) : null}
        <span>Identity {shortId(unifiedId)}</span>
      </nav>

      {/* HERO */}
      <div className="id-hero">
        <div className="id-hero-left">
          {profile.faceCropUrl ? (
            <FaceImg src={profile.faceCropUrl} alt="" className="id-hero-photo" />
          ) : (
            <div className="id-hero-photo-placeholder"><Fingerprint size={32} /></div>
          )}
          {/* Identity Strength ring */}
          <div className="id-strength">
            <svg viewBox="0 0 36 36" className="id-strength-ring">
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none" stroke="#eee" strokeWidth="3" />
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none" stroke={strengthInfo.color} strokeWidth="3"
                strokeDasharray={`${identityStrength}, 100`}
                strokeLinecap="round" />
            </svg>
            <div className="id-strength-label">
              <span className="id-strength-val">{identityStrength}</span>
              <span className="id-strength-text" style={{ color: strengthInfo.color }}>{strengthInfo.label}</span>
            </div>
          </div>
        </div>

        <div className="id-hero-body">
          <div className="id-hero-title">
            <h2>Identity Profile</h2>
            <span className="id-hero-uid">{unifiedId}</span>
            <span className={`id-hero-badge ${profile.confidence >= 0 ? 'id-hero-badge--active' : 'id-hero-badge--merged'}`}>
              {profile.confidence >= 0 ? 'Active' : 'Merged'}
            </span>
          </div>
          <div className="id-hero-sub">
            <span><Clock size={11} /> First seen {fmtRel(profile.createdAt)}</span>
            <span><Eye size={11} /> Last seen {fmtRel(profile.lastSeenAt)}</span>
            <span><Monitor size={11} /> {devices.length} device{devices.length !== 1 ? 's' : ''}</span>
            {profile.demographics ? (
              <span title={`gender ${Math.round((profile.demographics.genderShare ?? 0) * 100)}% of ${profile.demographics.nGender} votes${profile.demographics.ageMedian != null ? ` · age IQR ${profile.demographics.ageIqr} over ${profile.demographics.nAge} samples` : ''}`}>
                <Users size={11} /> {profile.demographics.gender}{profile.demographics.ageMedian != null ? `, ${profile.demographics.ageMedian}y` : ''}
              </span>
            ) : (
              <span style={{ opacity: 0.55 }} title="Below quality floor (n<5 gated samples)"><Users size={11} /> demographics: insufficient data</span>
            )}
          </div>

          {/* Status badges */}
          <div className="id-hero-badges">
            {profile.hasOracleEmbedding ? (
              <span className="id-badge id-badge--oracle">
                <Zap size={10} /> Oracle Verified
                {profile.oracleUpdatedAt && <span className="id-badge-sub">{fmtRel(profile.oracleUpdatedAt)}</span>}
              </span>
            ) : (
              <span className="id-badge id-badge--pending">
                <Cpu size={10} /> Awaiting Oracle
              </span>
            )}
            {profile.crossDeviceCount > 1 && (
              <span className="id-badge id-badge--crossdevice">
                <Monitor size={10} /> {profile.crossDeviceCount}-Device
              </span>
            )}
            {profile.contaminatedAt && (
              <span className="id-badge id-badge--contaminated">
                <AlertTriangle size={10} /> Contaminated {fmtRel(profile.contaminatedAt)}
              </span>
            )}
            {mergeSummary && (
              <span className="id-badge id-badge--merged-count">
                <GitMerge size={10} /> {mergeSummary.count} Merged
              </span>
            )}
          </div>

          {/* Stat bar */}
          <div className="id-hero-stats">
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{(profile.rideCount ?? 0).toLocaleString()}</div>
              <div className="id-hero-stat-lbl" title={`${profile.totalSessions.toLocaleString()} raw fragments`}>Rides</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{profile.totalImpressions.toLocaleString()}</div>
              <div className="id-hero-stat-lbl">Impressions</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{profile.confidence ? (profile.confidence * 100).toFixed(0) + '%' : '\u2014'}</div>
              <div className="id-hero-stat-lbl">Confidence</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{gaze.verifiedViews.toLocaleString()}</div>
              <div className="id-hero-stat-lbl">Gaze Views</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{profile.faceIdCount || 0}</div>
              <div className="id-hero-stat-lbl">Face IDs</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{profile.exemplarCount || 0}</div>
              <div className="id-hero-stat-lbl">Exemplars</div>
            </div>
            <div className="id-hero-stat">
              <div className="id-hero-stat-val">{faceCrops.length}</div>
              <div className="id-hero-stat-lbl">Face Crops</div>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="id-toolbar">
        {TIME_WINDOWS.map(tw => (
          <button key={tw.hours} className={`id-tw-btn${timeWindow === tw.hours ? ' id-tw-btn--active' : ''}`}
            onClick={() => { setTimeWindow(tw.hours); setSessionPage(0); }}>
            {tw.label}
          </button>
        ))}
        <div className="id-toolbar-right">
          <button className="id-refresh-btn" onClick={fetchData} disabled={loading}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* IDENTITY RESOLUTION SUMMARY */}
      {mergeSummary && (
        <div className="id-card id-card--full id-resolution-card">
          <div className="id-card-title"><Brain size={13} /> Identity Resolution</div>
          <div className="id-resolution-top">
            <div className="id-resolution-stats">
              <div className="id-resolution-stat">
                <div className="id-resolution-val">{mergeSummary.count}</div>
                <div className="id-resolution-lbl">Profiles Merged</div>
              </div>
              <div className="id-resolution-stat">
                <div className="id-resolution-val">{mergeSummary.totalAbsorbed}</div>
                <div className="id-resolution-lbl">Sessions Absorbed</div>
              </div>
              {mergeSummary.avgCosine != null && (
                <div className="id-resolution-stat">
                  <div className="id-resolution-val">{mergeSummary.avgCosine.toFixed(3)}</div>
                  <div className="id-resolution-lbl">Avg Cosine</div>
                </div>
              )}
              {mergeSummary.minCosine != null && (
                <div className="id-resolution-stat">
                  <div className="id-resolution-val" style={{ fontSize: '0.85rem' }}>{mergeSummary.minCosine.toFixed(3)} &mdash; {mergeSummary.maxCosine.toFixed(3)}</div>
                  <div className="id-resolution-lbl">Cosine Range</div>
                </div>
              )}
            </div>
            <div className="id-merge-type-row">
              {Object.entries(mergeSummary.byType).sort((a, b) => b[1] - a[1]).map(([lbl, cnt]) => {
                const info = MERGE_LABEL_INFO[lbl] || { label: lbl, bg: '#f3f4f6', color: '#555', icon: '\u2753' };
                return (
                  <span key={lbl} className="id-merge-type-pill" style={{ background: info.bg, color: info.color }}>
                    {info.icon} {info.label}: {cnt}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Cosine distribution bar chart */}
          {mergeSummary.cosines.length > 1 && (
            <div className="id-cosine-distrib">
              <div className="id-cosine-distrib-label">Cosine Distribution</div>
              <div className="id-cosine-bar-row">
                {mergeSummary.cosines.sort((a, b) => b - a).map((c, i) => {
                  const h = Math.max(8, (c / 1.0) * 60);
                  const color = c >= 0.8 ? '#16a34a' : c >= 0.65 ? '#2563eb' : c >= 0.55 ? '#d97706' : '#ef4444';
                  return (
                    <div key={i} className="id-cosine-bar-item" title={c.toFixed(3)}>
                      <div className="id-cosine-bar" style={{ height: h, background: color }} />
                      <span className="id-cosine-bar-val">{c.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Merge history items */}
          <div className="id-merge-list">
            {mergeHistory.map(m => {
              const info = MERGE_LABEL_INFO[m.mergeLabel] || { label: m.mergeLabel || 'Unknown', bg: '#f3f4f6', color: '#555', icon: '\u2753' };
              return (
                <div key={m.unifiedId} className="id-merge-item">
                  <div className="id-merge-photo">
                    {m.faceCropUrl ? (
                      <FaceImg src={m.faceCropUrl} alt="" />
                    ) : (
                      <div className="id-merge-photo-empty"><Fingerprint size={14} /></div>
                    )}
                  </div>
                  <div className="id-merge-info">
                    <div className="id-merge-top-row">
                      <span className="id-mono">{shortId(m.unifiedId)}</span>
                      <span className="id-merge-method" style={{ background: info.bg, color: info.color }}>
                        {info.icon} {info.label}
                      </span>
                      {m.mergeCosine != null && (
                        <span className="id-merge-cosine" title="Oracle cosine similarity">
                          <Shield size={9} /> {m.mergeCosine.toFixed(3)}
                        </span>
                      )}
                    </div>
                    <div className="id-merge-meta">
                      {m.sessionsContributed > 0 && <span>{m.sessionsContributed} session{m.sessionsContributed !== 1 ? 's' : ''}</span>}
                      {m.mergedAt && <span>Merged {fmtRel(m.mergedAt)}</span>}
                      {m.preMergeSessions != null && <span title="Sessions before merge">Pre-merge: {m.preMergeSessions}s</span>}
                      <span>Created {fmtRel(m.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ROW: Devices + Face Crops */}
      <div className="id-grid">
        <div className="id-card">
          <div className="id-card-title"><Monitor size={13} /> Cross-Device Presence</div>
          {devices.length === 0 ? (
            <div className="id-empty">No device data</div>
          ) : (
            <table className="id-devices-tbl">
              <thead>
                <tr><th>Device</th><th>Fragments</th><th>Last Seen</th><th>Impressions</th><th>Gaze</th><th>Confidence</th></tr>
              </thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d.deviceId}>
                    <td>
                      <Link to={`/ggos-devices/${d.deviceId}?section=perception`} className="id-device-link">
                        {d.deviceId}
                      </Link>
                    </td>
                    <td>{d.sessionCount}</td>
                    <td>{fmtRel(d.lastSeen)}</td>
                    <td>{d.totalImpressions.toLocaleString()}</td>
                    <td>{d.totalGazeViews}</td>
                    <td>{d.avgFaceConfidence ? (d.avgFaceConfidence * 100).toFixed(0) + '%' : '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="id-card">
          <div className="id-card-title"><Camera size={13} /> Face Crops ({faceCrops.length}{totalCropCount > faceCrops.length ? ` of ${totalCropCount}` : ''})</div>
          {faceCrops.length === 0 ? (
            <div className="id-empty">No face crops available</div>
          ) : (
            <div className="id-crops">
              {faceCrops.filter(c => c.url).map((c, i) => (
                <div key={i} className="id-crop-card" onClick={() => setLightbox(c)}>
                  <FaceImg src={c.url} alt="" />
                  <div className="id-crop-device">{c.deviceId?.split('-').pop() || ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ROW: Expression + Gaze */}
      <div className="id-grid">
        <div className="id-card">
          <div className="id-card-title"><Smile size={13} /> Expression Profile</div>
          {expressionDistribution.length === 0 ? (
            <div className="id-empty">No expression data</div>
          ) : (
            <div className="id-expr-list">
              {expressionDistribution.slice(0, 12).map(e => (
                <div key={`${e.expression}-${e.adRelevance}`} className="id-expr-row">
                  <span className="id-expr-label" style={{ color: MOOD_COLORS[e.expression] || '#888' }}>
                    {e.expression?.toLowerCase()}
                  </span>
                  <div className="id-expr-bar-wrap">
                    <div className="id-expr-bar" style={{
                      width: `${pct(e.count, exprMax)}%`,
                      background: MOOD_COLORS[e.expression] || '#94a3b8',
                    }} />
                  </div>
                  <span className="id-expr-cnt">{e.count}</span>
                  {e.adRelevance && (
                    <span className="id-expr-relevance" style={{
                      background: `${RELEVANCE_COLORS[e.adRelevance] || '#94a3b8'}18`,
                      color: RELEVANCE_COLORS[e.adRelevance] || '#888',
                    }}>
                      {e.adRelevance[0]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="id-card">
          <div className="id-card-title"><Crosshair size={13} /> Gaze Engagement</div>
          <div className="id-gaze-stats">
            <div className="id-gaze-stat">
              <div className="id-gaze-stat-val">{gaze.totalGazeEvents.toLocaleString()}</div>
              <div className="id-gaze-stat-lbl">Total Events</div>
            </div>
            <div className="id-gaze-stat">
              <div className="id-gaze-stat-val">{gaze.verifiedViews}</div>
              <div className="id-gaze-stat-lbl">Verified Views</div>
            </div>
            <div className="id-gaze-stat">
              <div className="id-gaze-stat-val">{fmtDuration(gaze.avgDwellMs)}</div>
              <div className="id-gaze-stat-lbl">Avg Dwell</div>
            </div>
            <div className="id-gaze-stat">
              <div className="id-gaze-stat-val">{gaze.avgGazeConfidence ? (gaze.avgGazeConfidence * 100).toFixed(0) + '%' : '\u2014'}</div>
              <div className="id-gaze-stat-lbl">Gaze Confidence</div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW: Activity + Mood + Embedding Quality */}
      <div className="id-grid id-grid--3">
        <div className="id-card">
          <div className="id-card-title"><BarChart2 size={13} /> Activity Timeline</div>
          {activityTimeline.length === 0 ? (
            <div className="id-empty">No activity in selected window</div>
          ) : (
            <div className="id-activity">
              {activityTimeline.map((a, i) => (
                <div key={i} className="id-activity-row">
                  <span className="id-activity-day">{fmtDate(a.day)}</span>
                  <div className="id-activity-bar-wrap">
                    <div className="id-activity-bar" style={{
                      width: `${pct(a.sessions, actMax)}%`,
                      background: '#6366f1',
                    }} />
                  </div>
                  <span className="id-activity-val" title="fragments/day">{a.sessions}f</span>
                  <span className="id-activity-val" style={{ color: '#16a34a' }}>{a.impressions}i</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="id-card">
          <div className="id-card-title"><TrendingUp size={13} /> Mood Over Time</div>
          {moodTimeline.length === 0 ? (
            <div className="id-empty">No mood data in selected window</div>
          ) : (
            <div className="id-mood-tl">
              {groupMoodByHour(moodTimeline).map((row, i) => (
                <div key={i} className="id-mood-tl-row">
                  <span className="id-mood-tl-time">{fmtHour(row.hour)}</span>
                  <div className="id-mood-tl-tags">
                    {row.moods.map((m, j) => (
                      <span key={j} className="id-mood-tl-tag" style={{
                        color: MOOD_COLORS[m.expression] || '#888',
                        background: `${MOOD_COLORS[m.expression] || '#94a3b8'}15`,
                      }}>
                        {m.expression?.toLowerCase()} x{m.count}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="id-card">
          <div className="id-card-title"><Target size={13} /> Embedding Quality</div>
          {(!embeddingQuality || embeddingQuality.length === 0) ? (
            <div className="id-empty">No embedding data in selected window</div>
          ) : (
            <div className="id-eq-list">
              {embeddingQuality.map((eq, i) => {
                const alignPct = eq.avgAlignment ? Math.round(eq.avgAlignment * 100) : 0;
                const alignColor = alignPct >= 80 ? '#16a34a' : alignPct >= 60 ? '#d97706' : '#ef4444';
                return (
                  <div key={i} className="id-eq-row">
                    <span className="id-eq-day">{fmtDate(eq.day)}</span>
                    <div className="id-eq-bar-wrap">
                      <div className="id-eq-bar" style={{ width: `${alignPct}%`, background: alignColor }} />
                    </div>
                    <span className="id-eq-pct" style={{ color: alignColor }}>{alignPct}%</span>
                    <span className="id-eq-detail" title={`${eq.embeddings} embeddings, ${eq.uniqueHashes} unique`}>
                      {eq.embeddings}e / {eq.uniqueHashes}u
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* SESSIONS TABLE */}
      <div className="id-card" style={{ marginBottom: 16 }}>
        <div className="id-card-title"><Activity size={13} /> Rides ({rides.length})</div>
        {rides.length === 0 ? (
          <div className="id-empty">No rides derived yet</div>
        ) : (
          <table className="id-sessions-tbl">
            <thead>
              <tr>
                <th>Ride</th>
                <th>Device</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Presence</th>
                <th>Fragments</th>
                <th>Flapping</th>
              </tr>
            </thead>
            <tbody>
              {rides.slice(0, 25).map(r => {
                // fragment_seconds / covered_seconds: 1.0 = clean single track;
                // higher = concurrent flapping fragments (device person-change bug)
                const flap = r.coveredSeconds > 0 ? r.fragmentSeconds / r.coveredSeconds : 1;
                const flapColor = flap > 1.5 ? '#dc2626' : flap > 1.1 ? '#d97706' : '#16a34a';
                return (
                  <tr key={r.rideSessionId}>
                    <td><span className="id-mono">{shortId(r.rideSessionId)}</span></td>
                    <td>
                      <Link to={`/ggos-devices/${r.deviceId}?section=perception`} className="id-session-device">
                        {r.deviceId}
                      </Link>
                    </td>
                    <td title={r.startedAt}>{fmtDateTime(r.startedAt)}</td>
                    <td>{r.endedAt ? fmtDuration(r.durationMs) : <span style={{ color: '#16a34a' }}>ongoing</span>}</td>
                    <td title="Observed presence (union of fragment intervals)">{fmtDuration(r.coveredSeconds * 1000)}</td>
                    <td>{r.fragmentCount}</td>
                    <td><span style={{ color: flapColor, fontWeight: 600 }}>{flap.toFixed(2)}×</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Session fragments — the raw 3-second-scale facts rides are stitched from */}
      <div className="id-card" style={{ marginBottom: 16 }}>
        <div className="id-card-title"><Activity size={13} /> Session Fragments ({sessions.length})</div>
        {sessions.length === 0 ? (
          <div className="id-empty">No sessions</div>
        ) : (
          <>
            <table className="id-sessions-tbl">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Device</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Events</th>
                  <th>Mood</th>
                  <th>Impressions</th>
                  <th>Gaze</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedSessions.map(s => {
                  const reconBadge = RECON_BADGES[s.reconciliationStatus] || { label: s.reconciliationStatus || '\u2014', bg: '#f3f4f6', color: '#888' };
                  return (
                    <tr key={s.sessionId}>
                      <td><span className="id-mono">{shortId(s.sessionId)}</span></td>
                      <td>
                        <Link to={`/ggos-devices/${s.deviceId}?section=perception`} className="id-session-device">
                          {s.deviceId}
                        </Link>
                      </td>
                      <td title={s.startedAt}>{fmtDateTime(s.startedAt)}</td>
                      <td>{fmtDuration(s.durationMs)}</td>
                      <td>
                        <span className="id-evts">
                          {s.faceEvents > 0 && <span title="Faces"><Users size={10} /> {s.faceEvents}</span>}
                          {s.gazeEvents > 0 && <span title="Gaze" style={{ color: '#16a34a' }}><Eye size={10} /> {s.gazeEvents}</span>}
                          {s.moodEvents > 0 && <span title="Mood" style={{ color: '#d97706' }}><Smile size={10} /> {s.moodEvents}</span>}
                          {s.totalEvents === 0 && <span style={{ color: '#ccc' }}>&mdash;</span>}
                        </span>
                      </td>
                      <td>
                        {s.moodAggregate ? (
                          <span className="id-mood-tag" style={{ color: MOOD_COLORS[s.moodAggregate?.toUpperCase()] || '#888' }}>
                            {s.moodAggregate}
                          </span>
                        ) : <span style={{ color: '#ccc' }}>&mdash;</span>}
                      </td>
                      <td>{s.impressionsShown || '\u2014'}</td>
                      <td>{s.gazeVerifiedViews || '\u2014'}</td>
                      <td>
                        <span className="id-recon-badge" style={{ background: reconBadge.bg, color: reconBadge.color }}>
                          {reconBadge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="id-pagination">
                <button className="id-page-btn" disabled={sessionPage === 0}
                  onClick={() => setSessionPage(p => p - 1)}>
                  <ChevronLeft size={12} /> Prev
                </button>
                <span>{sessionPage + 1} / {totalPages}</span>
                <button className="id-page-btn" disabled={sessionPage >= totalPages - 1}
                  onClick={() => setSessionPage(p => p + 1)}>
                  Next <ChevronRight size={12} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="id-lightbox" onClick={() => setLightbox(null)}>
          <div className="id-lightbox-inner" onClick={e => e.stopPropagation()}>
            <FaceImg src={lightbox.url} alt="" />
            <div className="id-lightbox-meta">
              <div><strong>Device:</strong> {lightbox.deviceId}</div>
              <div><strong>Captured:</strong> {fmtDateTime(lightbox.createdAt)}</div>
              {lightbox.alignmentScore && <div><strong>Alignment:</strong> {(lightbox.alignmentScore * 100).toFixed(1)}%</div>}
              <div><strong>Hash:</strong> <span className="id-mono">{shortId(lightbox.signatureHash)}</span></div>
            </div>
            <button className="id-lightbox-close" onClick={() => setLightbox(null)}>
              <X size={12} /> Close
            </button>
          </div>
        </div>
      )}

      {/* 3D Face Mesh Viewer */}
      {unifiedId && (
        <div className="id-section-card" style={{ marginTop: 24 }}>
          <div className="id-section-hdr"><Box size={14} /> 3D Face Mesh</div>
          <FaceMeshViewer unifiedId={unifiedId} thingName={fromDevice || data?.devices?.[0]?.deviceId || null} />
        </div>
      )}
    </div>
  );
}

/* Group mood timeline entries by hour */
function groupMoodByHour(timeline) {
  const map = new Map();
  for (const item of timeline) {
    const key = item.hour;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ expression: item.expression, count: item.count });
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .slice(-24)
    .map(([hour, moods]) => ({ hour, moods: moods.sort((a, b) => b.count - a.count).slice(0, 4) }));
}
